import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { pdfjs } from 'react-pdf';
import clsx from 'clsx';
import './meetingUploadFile.scss';
import Api from '@/api/api';
import { useUserStore } from '@/stores/user.ts';
import { ParticipantRole } from '@/consts/meeting';
import { EVENTS } from '@/consts/events.ts';
import { useDataChannel } from '@livekit/components-react';
import { ReceivedDataMessage } from '@livekit/components-core';
import { useMeetingStore } from '@/stores/meeting.ts';
import { useStudyModeStore } from '@/stores/studyMode.ts';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
  Box,
  DefaultCursor,
  TLCursorProps,
  TLAssetId,
  TLShapeId,
  SVGContainer,
  sortByIndex,
  TLComponents,
  TLShapePartial,
  TLImageShape,
  Tldraw,
  getIndicesBetween,
  react,
  track,
  useEditor,
  TLUserPreferences,
  useTldrawUser,
  Editor,
  useValue
} from 'tldraw';
import { useMultiplayerSync } from '@/hooks/useMultiplayerSync';
import { useBoardPdfStore } from '@/stores/boardPdf';
import { IsFocusedWatcher } from '@/components/MeetingBoard';
import { tldrawLicenseKey } from '@/consts/license.ts';
import { useMeetingFileUpload } from '@/hooks/useMeetingFileUpload';
import { usePdfManager } from '@/hooks/usePdfManager';
import { DocProps } from '@/types/api';

import { getAssetUrlsByImport } from '@tldraw/assets/imports.vite';
import CustomFileToolbar from '@/components/MeetingUploadFile/components/CustomFileToolbar';

const assetUrls = getAssetUrlsByImport();

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface FileUpdateMessage {
  action: 'update' | 'remove';
  activeFile?: DocProps;
}

interface PdfPage {
  src: string;
  bounds: Box;
  assetId: TLAssetId;
  shapeId: TLShapeId;
}

interface Pdf {
  name: string;
  pages: PdfPage[];
  source: string;
}

interface IsFocusedWatcherProps {
  id: string;
  setActiveId: (id: string) => void;
}

interface ViewedBoardProps extends IsFocusedWatcherProps {
  pdfFile: string;
  activeId: string | null;
  setIsLoadingPdf: (status: boolean) => void;
}

interface CustomToolbarProps {
  pdf: Pdf;
  unActiveFile: () => void;
}

interface MeetingUploadFileProps {
  id: string;
  activeId: string | null;
  setActiveId: (id: string) => void;
}

interface PdfEditorProps extends CustomToolbarProps, MeetingUploadFileProps {
  setIsLoadingPdf: (status: boolean) => void;
}

interface PageOverlayScreenProps {
  pdfPagesBounds: Box[];
}

const pageSpacing = 32;

function ViewedBoard({ pdfFile, id, activeId, setActiveId, setIsLoadingPdf }: ViewedBoardProps) {
  const userUid = useUserStore((state) => state.userUid);
  const role = useUserStore((state) => state.role);
  const userName = useUserStore((state) => state.userName);
  const [pdfPagesBounds, setPdfPagesBounds] = useState<Box[]>();

  const meeting = useMeetingStore((state) => state.meeting);

  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);

  const [editor, setEditor] = useState<Editor | null>();

  const [userPreferences] = useState<TLUserPreferences>({
    id: userUid,
    name: userName
  });

  const { activeDoc } = useBoardPdfStore();

  const roomId = `tldraw-meeting-pdf-${meeting?.uuid}-${meeting?.author_uuid}-${activeDoc?.id}`;
  const store = useMultiplayerSync(roomId, userPreferences);
  const user = useTldrawUser({ userPreferences });

  const isReadonly = role !== ParticipantRole.TEACHER;

  useEffect(() => {
    setIsLoadingPdf(true);
    setPdfPagesBounds(undefined);

    async function loadPdfPages() {
      try {
        const pdf = await pdfjs.getDocument(pdfFile).promise;
        const pages: Box[] = [];

        const scale = window.devicePixelRatio;
        const visualScale = 1.5;

        let top = 0;
        let widest = 0;

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: scale * visualScale });

          const width = viewport.width / scale;
          const height = viewport.height / scale;

          pages.push(new Box(0, top, width, height));
          top += height + pageSpacing;
          widest = Math.max(widest, width);
        }

        for (const page of pages) {
          page.x = (widest - page.w) / 2;
        }

        setPdfPagesBounds(pages);
      } catch (e) {
        console.error('Failed to load PDF pages', e);
        setIsLoadingPdf(false);
      }
    }

    loadPdfPages();
  }, [pdfFile, setIsLoadingPdf]);

  const isAuthorConnected = useValue(
    'isAuthorConnected',
    () => {
      if (!editor || !meeting?.author_uuid) return false;

      const peers = editor.getCollaborators();
      return peers.some((peer) => peer.userId === meeting.author_uuid);
    },
    [editor, meeting?.author_uuid]
  );

  useEffect(() => {
    if (!editor || !isAuthorConnected) return;
    if (!isPracticeMode) {
      if (meeting?.author_uuid) {
        editor.startFollowingUser(meeting.author_uuid);
        editor.setCameraOptions({ isLocked: true });
      }
    } else {
      editor.stopFollowingUser();
      editor.setCameraOptions({ isLocked: false });
    }
  }, [editor, isPracticeMode, meeting, activeId, pdfFile, isAuthorConnected]);

  const components = useMemo(() => {
    if (!pdfPagesBounds) {
      return;
    }
    const custom: TLComponents = {
      PageMenu: null,
      InFrontOfTheCanvas: () => <PageOverlayScreenView pdfPagesBounds={pdfPagesBounds} />
    };

    const readonly: TLComponents = isReadonly
      ? {
          Toolbar: null,
          ContextMenu: null,
          ActionsMenu: null,
          MainMenu: null,
          StylePanel: null,
          KeyboardShortcutsDialog: null,
          TopPanel: null,
          QuickActions: null,
          HelperButtons: null,
          SelectionForeground: null
        }
      : {};

    return {
      ...custom,
      ...readonly
    };
  }, [pdfPagesBounds, isReadonly]);

  const handleMount = (editor: Editor) => {
    setEditor(editor);

    editor.sideEffects.registerBeforeDeleteHandler('shape', (_, source) => {
      if (source === 'remote') return;
      if (isReadonly) return false;
      return;
    });

    editor.sideEffects.registerBeforeChangeHandler('shape', (prev, next, source) => {
      if (source === 'remote') return next;
      if (isReadonly) return prev;
      return next;
    });
  };

  // useEffect, который дождется вычисления границ страниц
  useEffect(() => {
    if (!editor || !pdfPagesBounds || pdfPagesBounds.length === 0) return;

    const targetBounds = pdfPagesBounds.reduce(
      (acc, pageBox) => acc.union(pageBox),
      pdfPagesBounds[0].clone()
    );

    function updateCameraBounds(isMobile: boolean) {
      if (!editor) return;

      editor.setCameraOptions({
        constraints: {
          bounds: targetBounds,
          padding: { x: isMobile ? 16 : 164, y: 64 },
          origin: { x: 0.5, y: 0 },
          initialZoom: 'fit-x-100',
          baseZoom: 'default',
          behavior: 'contain'
        }
      });
      editor.setCamera(editor.getCamera(), { reset: true });
    }

    let isMobile = editor.getViewportScreenBounds().width < 840;

    const unsubscribe = react('update camera', () => {
      const isMobileNow = editor.getViewportScreenBounds().width < 840;
      if (isMobileNow === isMobile) return;
      isMobile = isMobileNow;
      updateCameraBounds(isMobile);
    });

    updateCameraBounds(isMobile);

    setIsLoadingPdf(false);

    return () => {
      unsubscribe();
    };
  }, [editor, pdfPagesBounds, setIsLoadingPdf]);

  return (
    <Tldraw
      assetUrls={assetUrls}
      licenseKey={tldrawLicenseKey}
      store={store}
      user={user}
      onMount={handleMount}
      hideUi={isReadonly}
      options={{
        enableToolbarKeyboardShortcuts: !isReadonly,
        createTextOnCanvasDoubleClick: false
      }}
      components={{ ...components, ...useCollaborationComponents() }}
      acceptedImageMimeTypes={isReadonly ? [] : ['image/jpeg', 'image/png']}
      acceptedVideoMimeTypes={[]}
      maxAssetSize={10 * 1024 * 1024}
    >
      <IsFocusedWatcher id={id} setActiveId={setActiveId} />
    </Tldraw>
  );
}

function PdfEditor({
  pdf,
  id,
  activeId,
  setActiveId,
  unActiveFile,
  setIsLoadingPdf
}: PdfEditorProps) {
  const meeting = useMeetingStore((state) => state.meeting);
  const userUid = useUserStore((state) => state.userUid);
  const setPdfEditor = useBoardPdfStore((state) => state.setPdfEditor);
  const activeDoc = useBoardPdfStore((state) => state.activeDoc);
  const roomId = `tldraw-meeting-pdf-${meeting?.uuid}-${meeting?.author_uuid}-${activeDoc?.id}`;
  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);
  const pdfPagesBounds = useMemo(() => {
    if (!pdf?.pages) return [];
    return pdf.pages.map((page) => page.bounds);
  }, [pdf]);
  const userName = useUserStore((state) => state.userName);
  const [editor, setEditor] = useState<Editor | null>();

  const [userPreferences] = useState<TLUserPreferences>({
    id: userUid,
    name: userName
  });

  const store = useMultiplayerSync(roomId, userPreferences);
  const user = useTldrawUser({ userPreferences });
  const isReady = useMemo(() => {
    return pdf && store.status === 'synced-remote';
  }, [pdf, store]);

  useEffect(() => {
    setIsLoadingPdf(!isReady);
  }, [isReady, setIsLoadingPdf]);

  useEffect(() => {
    if (!editor || !pdf) return;

    if (activeId !== id) {
      editor.blur();
      if (!isPracticeMode) {
        editor.updateDocumentSettings({
          meta: { ...editor.getDocumentSettings().meta, isFocused: false }
        });
      }
    } else {
      editor.focus();
      if (!isPracticeMode) {
        editor.updateDocumentSettings({
          meta: { ...editor.getDocumentSettings().meta, isFocused: true }
        });
      }
    }
  }, [editor, activeId, pdf, unActiveFile]);

  const components = useMemo<TLComponents>(
    () => ({
      MainMenu: null,
      PageMenu: null,
      InFrontOfTheCanvas: () => <PageOverlayScreenView pdfPagesBounds={pdfPagesBounds} />,
      Toolbar: () => <CustomFileToolbar />,
      CollaboratorBrush: null,
      CollaboratorCursor: null,
      CollaboratorHint: null,
      CollaboratorScribble: null,
      CollaboratorShapeIndicator: null
    }),
    [pdf]
  );

  return (
    <>
      {isReady ? (
        <Tldraw
          assetUrls={assetUrls}
          licenseKey={tldrawLicenseKey}
          className="pdf-tldraw"
          onMount={(editor) => {
            setEditor(editor);
            setPdfEditor(editor);

            setActiveId(id);

            const shapeIds = pdf.pages.map((p) => p.shapeId);
            const shapeIdSet = new Set(shapeIds);

            const hasPdfShapes = pdf.pages.every((p) => editor.getShape(p.shapeId));
            const hasAnyAsset = pdf.pages.some((p) => editor.getAsset(p.assetId));

            if (!hasPdfShapes || !hasAnyAsset) {
              editor.createAssets(
                pdf.pages.map((page) => ({
                  id: page.assetId,
                  typeName: 'asset',
                  type: 'image',
                  meta: {},
                  props: {
                    w: page.bounds.w,
                    h: page.bounds.h,
                    mimeType: 'image/png',
                    src: page.src,
                    name: 'page',
                    isAnimated: false
                  }
                }))
              );

              editor.createShapes(
                pdf.pages.map(
                  (page): TLShapePartial<TLImageShape> => ({
                    id: page.shapeId,
                    type: 'image',
                    x: page.bounds.x,
                    y: page.bounds.y,
                    isLocked: true,
                    props: {
                      assetId: page.assetId,
                      w: page.bounds.w,
                      h: page.bounds.h
                    }
                  })
                )
              );

              // Don't let the user unlock the pages
              editor.sideEffects.registerBeforeChangeHandler('shape', (prev, next) => {
                if (!shapeIdSet.has(next.id)) return next;
                if (next.isLocked) return next;
                return { ...prev, isLocked: true };
              });

              // Make sure the shapes are below any of the other shapes
              const makeSureShapesAreAtBottom = () => {
                const shapes = shapeIds.map((id) => editor.getShape(id)!).sort(sortByIndex);
                const pageId = editor.getCurrentPageId();

                const siblings = editor.getSortedChildIdsForParent(pageId);
                const currentBottomShapes = siblings
                  .slice(0, shapes.length)
                  .map((id) => editor.getShape(id)!);

                if (currentBottomShapes.every((shape, i) => shape.id === shapes[i].id)) return;

                const otherSiblings = siblings.filter((id) => !shapeIdSet.has(id));
                const bottomSibling = otherSiblings[0];
                const lowestIndex = editor.getShape(bottomSibling)!.index;

                const indexes = getIndicesBetween(undefined, lowestIndex, shapes.length);
                editor.updateShapes(
                  shapes.map((shape, i) => ({
                    id: shape.id,
                    type: shape.type,
                    isLocked: shape.isLocked,
                    index: indexes[i]
                  }))
                );
              };

              makeSureShapesAreAtBottom();
              editor.sideEffects.registerAfterCreateHandler('shape', makeSureShapesAreAtBottom);
              editor.sideEffects.registerAfterChangeHandler('shape', makeSureShapesAreAtBottom);
            }

            // Constrain the camera to the bounds of the pages
            const targetBounds = pdf.pages.reduce(
              (acc, page) => acc.union(page.bounds),
              pdf.pages[0].bounds.clone()
            );

            const updateCameraBounds = (isMobile: boolean) => {
              editor.setCameraOptions({
                constraints: {
                  bounds: targetBounds,
                  padding: { x: isMobile ? 16 : 164, y: 64 },
                  origin: { x: 0.5, y: 0 },
                  initialZoom: 'fit-x-100',
                  baseZoom: 'default',
                  behavior: 'contain'
                }
              });
              editor.setCamera(editor.getCamera(), { reset: true });
            };

            let isMobile = editor.getViewportScreenBounds().width < 840;

            react('update camera', () => {
              const isMobileNow = editor.getViewportScreenBounds().width < 840;
              if (isMobileNow === isMobile) return;
              isMobile = isMobileNow;
              updateCameraBounds(isMobile);
            });

            updateCameraBounds(isMobile);
            setIsLoadingPdf(false);
          }}
          store={store}
          user={user}
          components={components}
        />
      ) : (
        <></>
      )}
    </>
  );
}

const PageOverlayScreenView = track(function PageOverlayScreen({
  pdfPagesBounds
}: PageOverlayScreenProps) {
  const editor = useEditor();

  const viewportPageBounds = editor.getViewportPageBounds();
  const viewportScreenBounds = editor.getViewportScreenBounds();

  const expandedScreenBounds = {
    w: viewportScreenBounds.w,
    h: viewportScreenBounds.h * 3
  };

  const relevantPageBounds = pdfPagesBounds
    .map((pageBox) => {
      if (!viewportPageBounds.collides(pageBox)) return null;
      const topLeft = editor.pageToViewport(pageBox);
      const bottomRight = editor.pageToViewport({ x: pageBox.maxX, y: pageBox.maxY });
      return new Box(topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y);
    })
    .filter((bounds): bounds is Box => bounds !== null);

  function pathForPageBounds(bounds: Box) {
    return `M ${bounds.x} ${bounds.y} L ${bounds.maxX} ${bounds.y} L ${bounds.maxX} ${bounds.maxY} L ${bounds.x} ${bounds.maxY} Z`;
  }

  const viewportPath = `M 0 0 
    L ${expandedScreenBounds.w} 0 
    L ${expandedScreenBounds.w} ${expandedScreenBounds.h} 
    L 0 ${expandedScreenBounds.h} Z`;

  return (
    <>
      <SVGContainer className="PageOverlayScreen-screen" style={{ height: '100vh' }}>
        <path
          d={`${viewportPath} ${relevantPageBounds.map(pathForPageBounds).join(' ')}`}
          fillRule="evenodd"
        />
      </SVGContainer>
      {relevantPageBounds.map((bounds, i) => (
        <div
          key={i}
          className="PageOverlayScreen-outline"
          style={{
            width: bounds.w,
            height: bounds.h,
            transform: `translate(${bounds.x}px, ${bounds.y}px)`
          }}
        />
      ))}
    </>
  );
});

const useCollaborationComponents = () => {
  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);
  const meeting = useMeetingStore((state) => state.meeting);

  const OnlyMainUserCursor = (props: TLCursorProps) => {
    if (props.userId !== meeting?.author_uuid) return null;
    return <DefaultCursor {...props} />;
  };

  return {
    CollaboratorBrush: null,
    CollaboratorCursor: !isPracticeMode ? OnlyMainUserCursor : null,
    CollaboratorHint: null,
    CollaboratorScribble: null,
    CollaboratorShapeIndicator: null
  };
};

function MeetingUploadFile({ id, activeId, setActiveId }: MeetingUploadFileProps) {
  const role = useUserStore((state) => state.role);
  const meeting = useMeetingStore((state) => state.meeting);
  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);

  const pdfGlobal = useBoardPdfStore((state) => state.pdfGlobal);
  const activeDoc = useBoardPdfStore((state) => state.activeDoc);
  const { setPdfGlobal, setExportPdf, setActiveDoc, setStudentDocs } = useBoardPdfStore.getState();

  const meetingUid = meeting?.uuid;
  const { handleFile, hover, setHover } = useMeetingFileUpload(meetingUid);
  const [isViewing, setIsViewing] = useState<boolean>(false);
  const isActive = activeId === id;

  const inputRef = useRef<HTMLInputElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { loadPdf, exportPdf } = usePdfManager(meetingUid, pdfContainerRef);

  const onFileUpdate = useCallback(
    (msg: ReceivedDataMessage) => {
      try {
        const { action, activeFile } = JSON.parse(
          new TextDecoder().decode(msg.payload)
        ) as FileUpdateMessage;

        if (action === 'update' && activeFile) {
          if (!isPracticeMode) {
            setActiveDoc(activeFile);
            setIsViewing(true);
          }
        } else if (action === 'remove') {
          if (!isPracticeMode) {
            setActiveDoc({ id: '', path: '' });
          }
        }
      } catch (e) {
        console.error('Failed to parse file update message', e);
      }
    },
    [isPracticeMode]
  );

  const { send: sendFileUpdate } = useDataChannel(EVENTS.FILE_UPLOAD, onFileUpdate);

  useEffect(() => {
    setExportPdf(exportPdf);
    return () => setExportPdf(null);
  }, [setExportPdf]);
  const [isLoadingPdf, setIsLoadingPdf] = useState<boolean>(false);

  const unActiveFile = async () => {
    if (!meetingUid || role !== ParticipantRole.TEACHER) {
      return;
    }
    await Api.setUnActiveFile(meetingUid);
    setPdfGlobal(undefined);
    setActiveId('');
    if (!isPracticeMode) {
      const message: FileUpdateMessage = { action: 'remove' };
      await sendFileUpdate(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }
  };

  useEffect(() => {
    if (!activeDoc || role !== ParticipantRole.TEACHER) return;

    const transitionToPracticeModeForStudent = async () => {
      const message: FileUpdateMessage = { action: 'remove', activeFile: { id: '', path: '' } };
      await sendFileUpdate(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    };

    const transitionToTheoryModeForStudent = async () => {
      const message: FileUpdateMessage = { action: 'update', activeFile: activeDoc };
      await sendFileUpdate(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    };

    if (isPracticeMode) {
      transitionToPracticeModeForStudent();
    } else {
      transitionToTheoryModeForStudent();
    }
  }, [isPracticeMode]);

  useEffect(() => {
    if (!activeDoc || !meetingUid) return;

    const syncPdf = async () => {
      const path = activeDoc.path.startsWith('/') ? activeDoc.path.slice(1) : activeDoc.path;
      const filePath = `${location.origin}/${path}`;
      const name = activeDoc.path.split('/').pop();
      const dataPdf = await loadPdf(`drawing-${name}`, filePath);
      setPdfGlobal(dataPdf);
      if (!isPracticeMode) {
        const message: FileUpdateMessage = { action: 'update', activeFile: activeDoc };
        await sendFileUpdate(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
      }
    };

    if (role === ParticipantRole.TEACHER) {
      if (activeDoc.path === '') {
        unActiveFile();
      } else {
        setPdfGlobal(undefined);
        syncPdf();
      }
    } else {
      setIsViewing(true);
    }
  }, [activeDoc]);

  useEffect(() => {
    if (!meetingUid) return;

    if (!isPracticeMode) {
      Api.getActiveFileForMeeting(meetingUid).then(async (file) => {
        if (file?.path) {
          if (role === ParticipantRole.TEACHER) {
            const studentFiles = await Api.getStudentFiles(meetingUid);
            setStudentDocs(studentFiles);
            setActiveDoc(file);
          } else {
            setActiveDoc(file);
            setIsViewing(true);
          }
        } else if (role !== ParticipantRole.TEACHER) {
          setActiveDoc({ id: '', path: '' });
        }
      });
    }
  }, [meetingUid, isPracticeMode, role]);

  return (
    <div className="container-upload-file" ref={containerRef}>
      <section
        className={clsx(
          'upload-file',
          hover && (activeDoc?.path === '' || !activeDoc) && 'upload-file__drop'
        )}
        ref={pdfContainerRef}
        {...(role === ParticipantRole.TEACHER && (activeDoc?.path === '' || !activeDoc)
          ? {
              onDragOver: (e) => {
                e.preventDefault();
                e.stopPropagation();
                setHover(true);
              },
              onDragLeave: () => setHover(false),
              onDrop: handleFile
            }
          : {})}
      >
        {activeDoc && activeDoc?.path !== '' ? (
          <div
            className={clsx('PdfEditor', isActive && 'active')}
            onPointerDown={role === ParticipantRole.TEACHER ? () => setActiveId(id) : () => {}}
          >
            {isLoadingPdf && (
              <ProgressSpinner
                className="pdf-progress-spinner"
                strokeWidth="4"
                animationDuration=".5s"
              />
            )}
            {!isViewing && pdfGlobal && (
              <PdfEditor
                pdf={pdfGlobal}
                id={id}
                activeId={activeId}
                setActiveId={setActiveId}
                unActiveFile={unActiveFile}
                setIsLoadingPdf={setIsLoadingPdf}
              />
            )}
            {isViewing && (
              <ViewedBoard
                pdfFile={`${location.origin}/${activeDoc.path.startsWith('/') ? activeDoc.path.slice(1) : activeDoc.path}`}
                id={id}
                activeId={activeId}
                setActiveId={setActiveId}
                setIsLoadingPdf={setIsLoadingPdf}
              />
            )}
          </div>
        ) : (
          <div className={clsx('no-file', hover ? 'drop-not-pointer' : 'drop-pointer')}>
            <input
              type="file"
              accept="application/pdf"
              hidden
              ref={inputRef}
              onChange={handleFile}
              disabled={role != ParticipantRole.TEACHER}
            />
            <div
              className={`upload-file__box ${role !== ParticipantRole.TEACHER ? 'disabled' : ''}`}
              onClick={() => inputRef.current?.click()}
            >
              <i
                className={clsx(
                  'pi',
                  role === ParticipantRole.TEACHER ? 'pi-upload' : 'pi-file-excel',
                  'upload-file__icon'
                )}
              />
              <p className="upload-file__text">
                {role === ParticipantRole.TEACHER ? 'Загрузить' : 'Файл отсутствует'}
              </p>
              <p className="upload-file__hint">
                {role === ParticipantRole.TEACHER
                  ? `Выберите файл PDF для ${isPracticeMode ? 'практической' : 'теоретической'} части`
                  : !isPracticeMode
                    ? 'Преподаватель пока не загрузил документ'
                    : 'Выберите документ из раздела "Файлы" для просмотра'}
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default MeetingUploadFile;
